import { useMutation } from '@apollo/client';
import moment from 'moment';
import React, { useState } from 'react';
import VersionChanges from './VersionChanges';
import { formatVersionDate } from './VersionChangesDropdown';
import * as styles from './VersionNameForm.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import { UIActions } from '../../ui/uiSlice';
import Logger from '../../utils/Logger';

const VERSION_NAME_MAX_LENGTH = 100;

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const UPDATE_HISTORY_NAME_MUTATION = gql(/** @complexity 2 */ `
  mutation updateHistoryName($historyId: ID!, $name: String!) {
    updateHistory(historyId: $historyId, name: $name) {
      id
      name
    }
  }
`);

interface VersionNameFormProps {
  onReset: () => void;
  currentVersionName: string | null;
  historyInsertedAt: string | null;
  historyId: string;
}

const VersionNameForm = ({
  onReset,
  currentVersionName,
  historyInsertedAt,
  historyId,
}: VersionNameFormProps) => {
  const dispatch = useAppDispatch();
  const [updateHistoryName] = useMutation(UPDATE_HISTORY_NAME_MUTATION);
  const [versionName, setVersionName] = useState(currentVersionName ?? '');

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const trimmedVersionName = versionName.trim();
        if (currentVersionName == null && !trimmedVersionName) {
          onReset();
          return;
        }

        if (trimmedVersionName !== currentVersionName) {
          try {
            await updateHistoryName({
              variables: {
                historyId,
                name: trimmedVersionName,
              },
              optimisticResponse: {
                updateHistory: {
                  id: historyId,
                  name: trimmedVersionName,
                },
              },
              update: (cache, { data }) => {
                if (!data) return;

                cache.modify({
                  id: cache.identify({
                    __typename: 'History',
                    id: historyId,
                  }),
                  fields: {
                    name: () => data.updateHistory.name || null,
                  },
                });
              },
            });
          } catch (error) {
            Logger.error(error);

            dispatch(
              UIActions.pushToast({
                level: 'error',
                title: 'Oops',
                message:
                  'Something went wrong while renaming the history. Please try again.',
              }),
            );
          }
        }

        onReset();
      }}
      className={styles.versionNameInputContainer}
    >
      <input
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onReset();
          }
        }}
        className={styles.versionNameInput}
        maxLength={VERSION_NAME_MAX_LENGTH}
        type="text"
        name="versionName"
        value={versionName}
        placeholder={
          (historyInsertedAt
            ? formatVersionDate(moment(historyInsertedAt))
            : null) ?? ''
        }
        onChange={(e) => setVersionName(e.target.value)}
        autoFocus
        aria-label="Version name"
      />
      <VersionChanges.BarButton
        disabled={false}
        icon={checkIcon}
        type="submit"
        aria-label="Save"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onReset();
          }
        }}
      />
    </form>
  );
};

export default VersionNameForm;
